import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import IdentifiersSection from '../../components/identity/IdentifiersSection.jsx';
import VerifiedBadge from '../../components/ui/VerifiedSeal.jsx';
import Alert from '../../components/ui/Alert.jsx';
import { EmptyState, Facts, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDate, fmtDay } from '../../utils/format.js';
import { coversText } from './peopleShared.jsx';
import { BackModal, CoverModal, EditPersonModal, LeftModal, RemovePersonModal, UncoverModal } from './personModals.jsx';

const CONFIRMATION = {
  pending: { tone: 'info', label: 'Email sent' },
  confirmed: { tone: 'good', label: 'Confirmed' },
  declined: { tone: 'bad', label: 'They said it isn’t them' },
  expired: { tone: 'neutral', label: 'Link expired' },
  cancelled: { tone: 'neutral', label: 'Replaced' },
  failed: { tone: 'bad', label: 'Couldn’t send' },
};

export default function Person() {
  const { personId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { organisation: current } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.notice ? { tone: 'success', text: location.state.notice } : null);
  const [modal, setModal] = useState(null);   // edit | cover | left | back | remove | {change: rel} | {uncover: rel}
  // Every hook goes above the early returns below, or React sees a different number of
  // hooks once the page has loaded (error #310, a blank page)
  const [sending, setSending] = useState(null);   // the identifier a confirmation is being sent for
  const orgBase = current ? `/portal/organisations/${current.id}` : '';
  const base = `${orgBase}/people/${personId}`;

  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(base)); setError(''); } catch (err) { setError(err.status === 404 ? 'This person isn’t listed.' : err.message); }
  }, [base, current]);
  useEffect(() => { load(); }, [load]);

  const back = { to: '/company/people', label: 'People' };
  if (error) return <div className="page"><PageHeader back={back} title="Person" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const { person: p, covers } = data;
  const left = p.status === 'left';
  const done = async (text) => { setModal(null); if (text) setNotice({ tone: 'success', text }); await load(); };

  const sendConfirmation = async (identifier) => {
    setSending(identifier.id);
    try {
      await api(`${base}/identifiers/${identifier.id}/confirm`, { method: 'POST' });
      setNotice({ tone: 'success', text: `We’ve emailed ${identifier.display_value} a link to confirm. It works for 7 days.` });
      await load();
    } catch (err) { setNotice({ tone: 'danger', text: err.message }); } finally { setSending(null); }
  };

  return (
    <div className="page">
      <PageHeader back={back} title={p.name}
        meta={<>{p.job_title && <span className="muted">{p.job_title}</span>}{left ? <Pill>Left {fmtDay(p.left_on)}</Pill> : <Pill tone="good">Active</Pill>}</>}
        actions={<>
          <button type="button" className="btn btn-secondary" onClick={() => setModal('edit')}>Edit details</button>
          {left
            ? <button type="button" className="btn btn-secondary" onClick={() => setModal('back')}>They&rsquo;re back</button>
            : <button type="button" className="btn btn-secondary" onClick={() => setModal('left')}>They&rsquo;ve left</button>}
          <button type="button" className="btn btn-danger-ghost" onClick={() => setModal('remove')}>Remove</button>
        </>} />

      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
      {left && <Alert tone="warn">{p.first_name} left on {fmtDay(p.left_on)}. Their addresses and numbers no longer count as yours.</Alert>}

      <IdentifiersSection
        key={data.identifiers.map((i) => `${i.id}:${i.verification}:${i.confirmation?.status}`).join(',')}
        title="Addresses and numbers"
        description="An email address counts once they’ve confirmed it from that inbox, or straight away if it’s on your verified domain. Phone numbers count as you’ve listed them."
        basePath={`${orgBase}/entities/${p.id}/identifiers`}
        ownerName={p.name}
        initial={data.identifiers}
        onChange={() => load()}
        types={['email_address', 'phone']}
        fixedVisibility="Never shown publicly. A check of this exact address or number says it’s yours, without naming anyone."
        modalDescription={`An address or number ${p.first_name} contacts people from.`}
        hideVisibility
        addLabel="Add address"
        extra={{
          header: 'Counts as yours?',
          render: (i) => {
            // Phone numbers can't be confirmed by email, so they count as soon as you list them
            if (i.type === 'phone') return <Pill tone="info" title="We can’t confirm a phone number by email, so it counts as soon as you list it.">Yes, as listed</Pill>;
            if (i.verification === 'verified') return <VerifiedBadge>{i.verified_via === 'mailbox_link' ? 'Confirmed' : 'Your domain'}</VerifiedBadge>;
            const c = i.confirmation && CONFIRMATION[i.confirmation.status];
            return (
              <span className="fact-confirm">
                {c ? <Pill tone={c.tone}>{c.label}</Pill> : <Pill tone="warn">Not confirmed</Pill>}
                <button type="button" className="link-button small" disabled={sending === i.id} onClick={() => sendConfirmation(i)}>{sending === i.id ? 'Sending…' : c ? 'Send again' : 'Send confirmation'}</button>
              </span>
            );
          },
        }}
      />

      <Section title="What they cover" description="Their addresses only count for these events (or the whole company), between these dates — so a former contractor can’t keep emailing on your behalf. Changed job? Use Change rather than removing and adding again."
        actions={<button type="button" className="btn btn-primary btn-small" onClick={() => setModal('cover')}>Add event or brand</button>}
        flush={covers.length > 0}>
        {covers.length === 0 ? <EmptyState>Nothing: their addresses don&rsquo;t count for any event yet.</EmptyState> : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Covers</th><th>How</th><th>Dates</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {covers.map((c) => (
                  <tr key={c.id}>
                    <td data-label="Covers">{coversText(c)}</td>
                    <td data-label="How">{c.kind_label}{c.role_label ? ` — ${c.role_label}` : ''}</td>
                    <td data-label="Dates">{c.valid_from || c.valid_until ? `${c.valid_from ? fmtDay(c.valid_from) : '…'} – ${c.valid_until ? fmtDay(c.valid_until) : '…'}` : 'Any time'}</td>
                    <td className="right row-actions">
                      <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ change: c })}>Change</button>
                      <button type="button" className="btn btn-small btn-danger-ghost" onClick={() => setModal({ uncover: c })}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Details">
        <Facts items={[['Name', p.name], ['Job title', p.job_title || '—'], ['Added', fmtDate(p.created_at)], ['Left', p.left_on ? fmtDay(p.left_on) : '—']]} />
      </Section>

      {modal === 'edit' && <EditPersonModal base={base} person={p} onClose={() => setModal(null)} onSaved={() => done('Saved.')} />}
      {modal === 'cover' && <CoverModal base={base} data={data} onClose={() => setModal(null)} onSaved={() => done('Added.')} />}
      {modal === 'left' && <LeftModal base={base} person={p} onClose={() => setModal(null)} onSaved={() => done(`${p.first_name}’s addresses no longer count from that date.`)} />}
      {modal?.change && <CoverModal base={base} data={data} cover={modal.change} onClose={() => setModal(null)} onSaved={() => done('Their role is updated.')} />}
      {modal === 'back' && <BackModal base={base} person={p} onClose={() => setModal(null)} onSaved={() => done('Their addresses count again.')} />}
      {modal?.uncover && <UncoverModal base={base} cover={modal.uncover} onClose={() => setModal(null)} onSaved={() => done('Removed.')} />}
      {modal === 'remove' && <RemovePersonModal base={base} person={p} onClose={() => setModal(null)} onRemoved={() => navigate('/company/people', { replace: true, state: { notice: `${p.name} has been removed.` } })} />}
    </div>
  );
}
